import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TNil } from '@app/app.interface';
import { forkJoin, Observable, of, shareReplay, throwError, timeout } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { validate, version } from 'uuid';

export type TFileResourceType = 'file' | 'image';

export interface IFileMetadata {
  url: string;
  size: number;
  fileName: string | null;
  mimeType: string | null;
}

// Shape of the dedicated metadata endpoints added in CampaignManagementAPI@7bad073
// (GET /files/{id}/metadata, /files/admin/{id}/metadata, /files/briefing/{exportId}/{id}/metadata,
// /files/export/{exportId}/{id}/metadata) - a JSON FileSchema, camelCased by the API interceptor
// (see ApiInterceptor.isJsonFileEndpoint). This is now the authoritative source for a file's
// name/mime-type/size: the byte-serving endpoints' Content-Disposition/-Type/-Length headers are
// kept only as a fallback (see toFileMetadata) for the odd case this call fails or races the blob
// fetch oddly - not because the headers themselves are unreliable, but because a dedicated
// metadata request is more directly what those endpoints exist for.
export interface IFileMetadataApiResponse {
  originalFilename: string;
  mimeType: string;
  sizeBytes: number | null;
}

interface IImageCacheEntry {
  observable: Observable<IFileMetadata>;
  objectUrl: string | null;
  refCount: number;
}

// /images/** now also passes through the Angular service worker's "images" dataGroup (see
// ngsw-config.json) once one is registered and controlling the page. That dataGroup has its own
// network timeout (cacheConfig.timeout), but that only bounds the network fetch itself - not the
// step where the service worker writes the response into Cache Storage before handing it back to
// the page (ngsw-worker.js's DataGroup.cacheResponse, awaited inline on a cache miss). A hung
// Cache Storage/IndexedDB write - the same category of "stuck service worker" failure this app
// has hit before - would leave that await never resolving, and the service worker's own timeout
// can't catch it because it already ran (and won) before the hang starts. This timeout is what
// actually bounds that: it wraps the whole round trip as observed from the page (fetch call in,
// response back), regardless of which internal step of the service worker - if any - is stuck, so
// a hang always surfaces as an error the existing retry-then-placeholder logic in
// ImageLoaderDirective already knows how to handle, instead of leaving a tile stuck on the
// transparent placeholder forever with no failure signal at all.
//
// Used ONLY by getImageUrlAndSize - the tile/avatar loading path that is what the service worker
// dataGroup above actually targets, and which has that retry-then-placeholder fallback to catch a
// timeout safely. This is deliberately NOT keyed off TFileResourceType/fileType: an earlier version
// of this file used a short timeout for getFile/getFileUrlAndSize whenever fileType === 'image' on
// the assumption that 'image' meant "small avatar/thumbnail", but fileType is actually assigned
// purely by file extension (see FileUploaderService.getFileTypeByExtension) - an arbitrary,
// possibly very large user-uploaded photo is just as much 'image' as a tiny avatar. Those callers
// (file-uploader, social-content-files) have no retry logic, so cutting them off at 15s risked
// silently failing legitimate large uploads/downloads over a slow connection. getImageUrlAndSize
// is the only path that's actually short-lived by nature (tile/avatar thumbnails) AND has a safe
// fallback if it times out, so it's the only one that gets the short timeout.
const IMAGE_FETCH_TIMEOUT_MS = 15000;

// Used by getFile/getFileUrlAndSize for every fileType (see IMAGE_FETCH_TIMEOUT_MS above for why
// this is no longer conditional on fileType). Deliberately far longer than the tile/avatar
// timeout: it still exists to catch the same "service worker cache write hangs forever" failure
// mode as a last resort, just tuned to not misfire on ordinary large/slow downloads that have no
// retry logic to fall back on.
const FILE_FETCH_TIMEOUT_MS = 60000;

@Injectable({
  providedIn: 'root',
})
export class ImageUtilitiesService {
  public static readonly transparentImage =
    'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEA' as const;

  // Keyed by the fully-resolved request URL (id + fileType + route context, see getApiUrl) so
  // multiple ImageLoaderDirective instances asking for the same image at the same time share one
  // network fetch and one object URL instead of each creating their own - defense in depth beyond
  // the tile grid no longer tearing itself down on refresh, for any other/future spot in the app
  // that might request the same image twice. refCount tracks how many live callers currently hold
  // a reference; the object URL is only revoked once it drops to zero (see releaseImage), so one
  // consumer releasing can never break the image for another consumer still displaying it.
  // Deliberately scoped to just this method (used exclusively by ImageLoaderDirective, and by
  // FileUploaderComponent to share the exact same in-flight request/cache entry for its
  // fileName/mimeType display needs in 'media' mode) rather than getFile/getFileUrlAndSize/getImage
  // below, which have other callers (file-uploader's download/preview actions, social-content-files,
  // admin feedback) with their own one-shot fetch-then-revoke lifecycles that sharing a cached
  // object URL out from under them would break.
  private readonly imageUrlCache = new Map<string, IImageCacheEntry>();

  private readonly authorizedApiUrl: string = '/images';
  private readonly authorizedFileApiUrl: string = '/files';
  private readonly adminApiUrl: string = '/images/admin/';
  private readonly adminFileApiUrl: string = '/files/admin/';
  private readonly unauthorizedCreatorApiUrl: string = '/images/briefing/';
  private readonly unauthorizedCreatorFileApiUrl: string = '/files/briefing/';
  private readonly unauthorizedExportApiUrl: string = '/images/export/';
  private readonly unauthorizedExportFileApiUrl: string = '/files/export/';

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
  ) {}

  // File/image ids are now opaque backend-issued UUIDs (the `files` table's primary key) rather
  // than base64-encoded storage paths, so validity is just "is this a well-formed UUID v4" - no
  // decoding involved. The backend no longer needs a "temp" marker either (see the removed isTemp
  // param that used to live here): a pending/never-attached file is now cleaned up by a backend
  // cleanup task based on the file row's own status/expires_at, not by the frontend inspecting the
  // id string, so callers no longer need (or can) tell temp and permanent ids apart from the id
  // alone.
  //
  // Only recognizes the *current* id format - old data that still holds a legacy base64 id (see
  // isValidLegacyFileId below) deliberately reads as invalid here, since this is also what a few
  // callers use to decide whether to attempt a create/update with the id (e.g.
  // FileUploaderService.createCustomFile's mock-file/validator plumbing). Display-only call sites
  // should use isDisplayableFileId instead, which also accepts a legacy id.
  public static isValidFileId(str: string | TNil): boolean {
    if (!str || str.trim() === '') {
      return false;
    }

    return validate(str) && version(str) === 4;
  }

  // Pre-migration, a remote file id was base64 of a storage path whose last '/'-separated
  // segment is "{uuid}_{originalName}.{ext}" (see git history's FileUploaderService.getFileInfo/
  // getFileName and ImageUtilitiesService.isBase64WithValidUUID) - e.g. decoding to
  // "681cdab9-ce24-4733-81ee-708bd8d8c933_Banner_1920x300.png", optionally behind a leading
  // "{user_group_id}/..." path prefix that isBase64WithValidUUID used to check instead (a
  // different, ancestor UUID - not the file's own). Splitting the last segment on the FIRST
  // underscore only (getLegacyFileName below relies on this too) is what actually recovers the
  // file's own id, since the original name can itself contain underscores. No backend migration
  // is planned to convert this stored data (campaign CTA templates/deliverables created before
  // the id-format change can permanently keep holding one of these), but the backend's read
  // endpoints still resolve it - so this is purely a display-time recognition check, never used
  // to gate a create/update/delete call.
  public static isValidLegacyFileId(str: string | TNil): boolean {
    const uuidSegment = ImageUtilitiesService.getLegacyIdUuidSegment(str);

    return !!uuidSegment && validate(uuidSegment) && version(uuidSegment) === 4;
  }

  // The pre-migration id's original filename, decoded straight out of the id itself - a legacy
  // byte-serving response isn't guaranteed to set Content-Disposition (and never will for this
  // old data, since no backend migration is planned), so this is the only reliable source for the
  // name; mime-type and size still come from the response's Content-Type/-Length headers (see
  // toFileMetadata) since those ARE reliably present. Only ever applies to a legacy id -
  // isValidLegacyFileId returns false immediately for a modern UUID (which carries no encoded
  // information at all), so this correctly returns null for one rather than attempting to decode
  // it as base64.
  public static getLegacyFileName(str: string | TNil): string | null {
    if (!ImageUtilitiesService.isValidLegacyFileId(str)) {
      return null;
    }

    const completeFileName = ImageUtilitiesService.getLegacyIdLastSegment(str!);
    const underscoreIndex = completeFileName.indexOf('_');

    if (underscoreIndex === -1) {
      return null;
    }

    return completeFileName.slice(underscoreIndex + 1) || null;
  }

  // A file's own display name can carry a leading "{uuid}_" prefix when it originated from this
  // app's own storage, whose physical filenames are "{uuid}_{originalName}" (see the backend's
  // generate_temp_path/_encode and getLegacyFileName above) - e.g. a user re-uploads a file they
  // previously downloaded from one of our own attachments, or a metadata source hands back the
  // stored filename verbatim instead of the original one. Unrelated to legacy id decoding (that
  // recovers a name FROM the id itself); this instead cleans up a name that already carries this
  // prefix baked directly into it, regardless of whether it came from a legacy or modern id, or a
  // local file the user just picked. Only strips when the part before the first underscore is an
  // exact UUID v4, so a name that merely happens to start with something uuid-shaped (but isn't
  // one) is left untouched.
  public static stripUuidPrefix(name: string): string {
    const underscoreIndex = name.indexOf('_');

    if (underscoreIndex === -1) {
      return name;
    }

    const potentialUuid = name.slice(0, underscoreIndex);

    return validate(potentialUuid) && version(potentialUuid) === 4 ? name.slice(underscoreIndex + 1) : name;
  }

  // Shared by isValidLegacyFileId/getLegacyFileName: the last '/'-separated segment of the
  // decoded id, e.g. "{uuid}_{name}.{ext}" out of "{user_group_id}/temp/{uuid}_{name}.{ext}" (or
  // the whole decoded string when there's no path prefix at all - Array.split on a string with no
  // separator just returns that string as the only element).
  //
  // atob() only recovers the raw bytes as a Latin-1 "binary string" - it has no notion of UTF-8,
  // so a byte sequence for a multi-byte character (e.g. the 2 UTF-8 bytes for "ü") would otherwise
  // come out as two separate mojibake characters. The backend encodes the original path with
  // Python's rel_path.encode() (UTF-8 by default - see CampaignCommons file_services._encode)
  // before base64-ing it, so decoding those bytes back through TextDecoder (UTF-8 by default,
  // non-fatal - it never throws, substituting U+FFFD for anything actually malformed) is what
  // correctly recovers an accented/non-ASCII original filename instead of garbling it. This can
  // never change the uuid segment on either side of this decode, since that's always plain ASCII.
  //
  // The backend also encodes with base64.urlsafe_b64encode (same file), which swaps the standard
  // alphabet's '+'/'/' for '-'/'_' - atob() only understands the standard alphabet and throws on
  // '-'/'_', so those need converting back before it ever sees them. This is rare in practice (a
  // filename needs one of a handful of unusual bytes, like '>' or '?', at the right alignment to
  // produce either character - see the git history's isBase64WithValidUUID, which has the same
  // gap), but silently mis-detecting a real legacy id as "not ours" is worse than the cost of
  // always normalizing first, and normalizing a string that never had either character is a no-op.
  private static getLegacyIdLastSegment(str: string): string {
    const standardBase64 = str.replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(standardBase64), (character) => character.charCodeAt(0));
    const decoded = new TextDecoder().decode(bytes);
    const segments = decoded.split('/');

    return segments[segments.length - 1];
  }

  // The part of the last segment before its first underscore - the file's own uuid when a name
  // follows it ("{uuid}_{name}.{ext}"), or the whole segment when it doesn't (a bare/temp id with
  // no name attached yet). Returns null rather than throwing for anything that isn't validly
  // base64 at all (an external URL, a static icon path, garbage) - validate()/version() above
  // then correctly reject it as not a UUID either way.
  private static getLegacyIdUuidSegment(str: string | TNil): string | null {
    if (!str || str.trim() === '') {
      return null;
    }

    try {
      const completeFileName = ImageUtilitiesService.getLegacyIdLastSegment(str);
      const underscoreIndex = completeFileName.indexOf('_');

      return underscoreIndex === -1 ? completeFileName : completeFileName.slice(0, underscoreIndex);
    } catch {
      return null;
    }
  }

  // "Is this actually one of our own files, so it's worth fetching and displaying" - true for
  // either a modern opaque UUID or a legacy base64 id (see isValidLegacyFileId), as opposed to an
  // external URL (a creator's social platform thumbnail, a static /icons/ path) or nothing at
  // all. Every display-only consumer (FileUploaderComponent.isRemoteFileId,
  // IconBadgeComponent.isNotUploadedImage/processedImageUrl, FileUploaderService.createCustomFile)
  // should check this rather than isValidFileId directly, so pre-migration data keeps displaying
  // without any backend data migration.
  public static isDisplayableFileId(str: string | TNil): boolean {
    return ImageUtilitiesService.isValidFileId(str) || ImageUtilitiesService.isValidLegacyFileId(str);
  }

  public static replaceImageDimensions(url: string | TNil, width: number, height: number): string | null {
    if (!url) {
      return null;
    }

    if (ImageUtilitiesService.isDisplayableFileId(url)) {
      return url;
    }

    const dimensionConstant = ImageUtilitiesService.getYoutubeDimensionConstant(width, height);

    return url
      .replace(/%{width}/g, width.toString())
      .replace(/%{height}/g, height.toString())
      .replace(/{width}/g, width.toString())
      .replace(/{height}/g, height.toString())
      .replace(/width=/g, `width=${width}`)
      .replace(/height=/g, `height=${height}`)
      .replace(/(?<![A-Za-z])w=/g, `w=${width}`)
      .replace(/(?<![A-Za-z])h=/g, `h=${height}`)
      .replace(/{resolution_constant}/g, dimensionConstant);
  }

  // Mirrors the backend's build_content_disposition (abstract_file_service.py): a plain quoted
  // filename="..." form for ASCII names, falling back to the RFC 5987 filename*=utf-8''... form
  // for anything else (percent-encoded). Returns null rather than throwing on anything
  // unrecognized - a missing/unparseable filename should degrade to "no name available", never
  // break the download/display it's feeding.
  public static parseContentDispositionFileName(header: string | null): string | null {
    if (!header) {
      return null;
    }

    const extendedMatch = /filename\*\s*=\s*[^']*''([^;]+)/i.exec(header);

    if (extendedMatch?.[1]) {
      try {
        return decodeURIComponent(extendedMatch[1].trim());
      } catch {
        return null;
      }
    }

    const quotedMatch = /filename\s*=\s*"([^"]*)"/i.exec(header);

    if (quotedMatch) {
      return quotedMatch[1];
    }

    const bareMatch = /filename\s*=\s*([^;]+)/i.exec(header);

    return bareMatch ? bareMatch[1].trim() : null;
  }

  public uploadImage(file: File): Observable<string> {
    return this.uploadFile(file, 'image');
  }

  public uploadFile(file: File, fileType: TFileResourceType): Observable<string> {
    const formData: FormData = new FormData();
    formData.append('file', file);

    return this.http.post<string>(this.getAuthorizedApiUrl(fileType), formData);
  }

  public getImage(id: string): Observable<string> {
    return this.getFile(id, 'image');
  }

  // Fetches just the raw bytes as a blob: URL - used by callers that only ever needed the object
  // URL (download links, list-mode/enlarged-image previews) and by getFileMetadata below (which
  // additionally combines this with the dedicated metadata endpoint - see there).
  public getFile(id: string, fileType: TFileResourceType): Observable<string> {
    return this.fetchBlob(id, fileType).pipe(map((response) => this.toFileMetadata(id, response).url));
  }

  // Combines two requests: the byte-serving GET (for the object URL, and as a fallback source for
  // name/mime-type/size via its Content-Disposition/-Type/-Length headers - see
  // build_content_disposition and s3_file_service.py/local_file_service.py) and the dedicated
  // metadata endpoint added in CampaignManagementAPI@7bad073 (the authoritative source - see
  // IFileMetadataApiResponse above). getMetadataApiUrl always resolves through the /files/ prefix
  // regardless of fileType, since there is no /images/.../metadata route - /images/ and /files/
  // reads share the same underlying file row and permission checks (see getApiUrl), so this works
  // for image ids too. The metadata call is allowed to fail independently (catchError -> null)
  // rather than failing the whole lookup - a still-missing/errored metadata row shouldn't block
  // showing the file itself, just fall back to what the headers can tell us.
  //
  // For a legacy base64 id specifically, that metadata call is skipped rather than fired and
  // caught: the endpoint is typed to a UUID at the route level (see getFileMetadataInfo), so it
  // can never resolve one no matter what base64 support the backend adds elsewhere - firing it
  // anyway would just be a guaranteed-failing request on every legacy attachment, and forkJoin
  // would wait on it to fail before ever emitting instead of resolving as soon as the (useful)
  // blob leg completes.
  public getFileMetadata(id: string, fileType: TFileResourceType): Observable<IFileMetadata> {
    const metadataInfo$: Observable<IFileMetadataApiResponse | null> = ImageUtilitiesService.isValidLegacyFileId(id)
      ? of(null)
      : this.getFileMetadataInfo(id).pipe(catchError(() => of(null)));

    return forkJoin([this.fetchBlob(id, fileType), metadataInfo$]).pipe(
      map(([response, metadataInfo]) => this.toFileMetadata(id, response, metadataInfo)),
    );
  }

  // Raw JSON call to the dedicated metadata endpoint on its own, for callers that want
  // name/mime-type/size without also downloading the file's bytes - getFileMetadata above builds
  // on this rather than duplicating the URL logic (skipping straight past it for a known-legacy
  // id, since it can never resolve one - see there), and FileUploaderComponent's
  // syncRemoteFileMeta calls this directly for its 'list' mode branch's modern-id case, which
  // never displays pixels and so has no reason to fetch the file's bytes at all.
  //
  // Bounded with the same timeout as fetchBlob below - without this, a request that never
  // errors and never completes (a stuck connection, a server that accepts but never answers)
  // would leave getFileMetadata's forkJoin waiting forever: catchError only converts an error
  // into a fallback, it doesn't impose a time limit on its own, so this leg needs its own bound
  // exactly like the blob leg already has. This is what guarantees FileUploaderComponent's
  // hasRemoteFileMetaError/remoteFileMeta pair always eventually settles one way or the other -
  // see syncRemoteFileMeta and isFileMetadataLoading there, which depend on that guarantee to
  // avoid showing a skeleton loader indefinitely.
  public getFileMetadataInfo(id: string): Observable<IFileMetadataApiResponse> {
    return this.http.get<IFileMetadataApiResponse>(this.getMetadataApiUrl(id)).pipe(timeout(FILE_FETCH_TIMEOUT_MS));
  }

  private fetchBlob(id: string, fileType: TFileResourceType): Observable<HttpResponse<Blob>> {
    const url = this.getApiUrl(id, fileType);
    const headers = { Accept: this.getAcceptHeader(fileType) };

    return this.http
      .get(url, { headers, observe: 'response', responseType: 'blob' })
      .pipe(timeout(FILE_FETCH_TIMEOUT_MS));
  }

  // Cached and reference-counted - see imageUrlCache above. Every call here MUST be matched by
  // exactly one later call to releaseImage(cacheKey), passing back the SAME cacheKey returned
  // here, regardless of whether this particular call ends up succeeding, still pending, or
  // erroring, or the reference count will drift.
  //
  // Returns the resolved cacheKey alongside the observable specifically so the caller can hold
  // onto it and pass it back unchanged to releaseImage later, rather than asking the caller to
  // re-derive it from id at release time via getApiUrl - getApiUrl depends on the CURRENT route
  // (this.router.url), which can legitimately be different by the time release happens (e.g. a
  // component's ngOnDestroy commonly fires as part of navigating away, after the route has
  // already changed). Re-deriving at release time could resolve to a different key than the one
  // actually acquired, either leaking the real entry or decrementing an unrelated one.
  public getImageUrlAndSize(id: string): { cacheKey: string; observable: Observable<IFileMetadata> } {
    const cacheKey = this.getApiUrl(id, 'image');
    const cached = this.imageUrlCache.get(cacheKey);

    if (cached) {
      cached.refCount++;

      return { cacheKey, observable: cached.observable };
    }

    const headers = { Accept: this.getAcceptHeader('image') };

    const observable: Observable<IFileMetadata> = this.http
      .get(cacheKey, { headers, observe: 'response', responseType: 'blob' })
      .pipe(
        timeout(IMAGE_FETCH_TIMEOUT_MS),
        map((response: HttpResponse<Blob>) => {
          const metadata = this.toFileMetadata(id, response);
          const entry = this.imageUrlCache.get(cacheKey);

          if (!entry) {
            // Defensive only: releaseImage() is designed to never delete a still-pending entry
            // (see below), so this shouldn't be reachable - but if it somehow is, revoke rather
            // than silently leak the URL we just created. Fail loudly instead of resolving with
            // it anyway - a "successful" result carrying a URL we just revoked would be worse
            // than an error, since every subscriber would get a value that looks valid but can
            // never actually be used.
            URL.revokeObjectURL(metadata.url);

            throw new Error(`Image cache entry for "${cacheKey}" was evicted before its fetch resolved`);
          }

          entry.objectUrl = metadata.url;

          if (entry.refCount <= 0) {
            // Every caller released before this fetch resolved (e.g. an extremely fast
            // mount/unmount) - nobody is tracking this URL anymore, so clean it up now that we
            // finally know what to revoke, instead of leaving it cached forever unreferenced.
            URL.revokeObjectURL(metadata.url);
            this.imageUrlCache.delete(cacheKey);
          }

          return metadata;
        }),
        catchError((error: unknown) => {
          // Don't let a transient failure poison the cache forever: shareReplay would otherwise
          // keep replaying this exact error to every future caller for this id, even ones that
          // start well after whatever caused it has resolved itself. Evicting here means the
          // next getImageUrlAndSize(id) call is a clean retry, not a replay of an old failure.
          this.imageUrlCache.delete(cacheKey);

          return throwError(() => error);
        }),
        shareReplay(1),
      );

    this.imageUrlCache.set(cacheKey, { objectUrl: null, observable, refCount: 1 });

    return { cacheKey, observable };
  }

  // Must be called exactly once for every getImageUrlAndSize(id) call once the caller no longer
  // needs the result, passing back the cacheKey that call returned - mirrors exactly when callers
  // used to call URL.revokeObjectURL directly. Decrements the shared reference count and only
  // revokes the underlying object URL - and only once, regardless of how many callers were
  // sharing it - once the last caller has released it.
  public releaseImage(cacheKey: string): void {
    const cached = this.imageUrlCache.get(cacheKey);

    if (!cached) {
      return;
    }

    cached.refCount--;

    if (cached.refCount > 0) {
      return;
    }

    if (!cached.objectUrl) {
      // Still in flight - leave the (now refCount 0) entry in place so the pending fetch can
      // still populate objectUrl; the map() above will revoke and evict it at that point if
      // refCount is still 0 by then. Deleting it here would orphan the object URL the in-flight
      // request is about to create, since nothing would be left to track it for eventual revoke.
      return;
    }

    URL.revokeObjectURL(cached.objectUrl);
    this.imageUrlCache.delete(cacheKey);
  }

  public getFileUrlAndSize(id: string, fileType: TFileResourceType): Observable<IFileMetadata> {
    return this.getFileMetadata(id, fileType);
  }

  public deleteImage(id: string): Observable<void> {
    return this.deleteFile(id, 'image');
  }

  public deleteFile(id: string, fileType: TFileResourceType): Observable<void> {
    return this.http.delete<void>(`${this.getAuthorizedApiUrl(fileType)}/${id}`);
  }

  // metadataInfo, when present, comes from the dedicated /metadata JSON endpoint
  // (getFileMetadataInfo) and is authoritative - it reads the File row directly, whereas the
  // Content-Disposition/-Type/-Length headers below depend on each storage backend consistently
  // setting them on every byte-serving response. Headers are kept as the fallback for getFile
  // (which never fetches metadataInfo, to avoid doubling requests on that call path) and for the
  // case getFileMetadataInfo itself failed (see getFileMetadata's catchError(() => of(null))).
  // id is only needed for that header-fallback branch's fileName: a legacy base64 id encodes its
  // own original filename (see getLegacyFileName) and a legacy blob response is never guaranteed
  // to set Content-Disposition at all (no backend migration is planned to add it retroactively),
  // so getLegacyFileName is tried first and parseContentDispositionFileName only covers a modern
  // id or an actual Content-Disposition header when one is present.
  private toFileMetadata(
    id: string,
    response: HttpResponse<Blob>,
    metadataInfo?: IFileMetadataApiResponse | null,
  ): IFileMetadata {
    const { body } = response;

    if (!(body instanceof Blob)) {
      throw new Error('Response is not a Blob');
    }

    if (metadataInfo) {
      return {
        // Coalesce an empty string to null rather than passing it straight through - every
        // consumer already treats '' and null the same way via truthy checks, but null is the
        // more honest "no name/type known" signal to hand out from here, matching what the
        // header-parsing fallback below already does for a missing Content-Disposition/-Type.
        fileName: metadataInfo.originalFilename || null,
        mimeType: metadataInfo.mimeType || null,
        size: metadataInfo.sizeBytes ?? body.size,
        url: URL.createObjectURL(body),
      };
    }

    const contentLengthHeader = response.headers.get('Content-Length');
    const size = contentLengthHeader ? Number(contentLengthHeader) : body.size;

    return {
      fileName:
        ImageUtilitiesService.getLegacyFileName(id) ||
        ImageUtilitiesService.parseContentDispositionFileName(response.headers.get('Content-Disposition')),
      mimeType: response.headers.get('Content-Type') || body.type || null,
      size: Number.isFinite(size) ? size : body.size,
      url: URL.createObjectURL(body),
    };
  }

  // Metadata (name/mime-type/size as JSON) only exists under the /files/ prefix (see
  // CampaignManagementAPI@7bad073) - there is no /images/{id}/metadata route, even though
  // /images/{id} and /files/{id} share identical resolve/permission logic for reads. So this
  // always forces fileType to 'file' regardless of the caller's actual TFileResourceType, which
  // still resolves through the correct own/admin/briefing/export prefix via getApiUrl's existing
  // this.router.url branching.
  private getMetadataApiUrl(id: string): string {
    return `${this.getApiUrl(id, 'file')}/metadata`;
  }

  private getApiUrl(id: string, fileType: TFileResourceType): string {
    const { url } = this.router;

    if (url.includes('campaigns/briefing/creator')) {
      const briefingId = url.split('/').pop();

      return `${this.getCreatorApiUrl(fileType)}${briefingId}/${id}`;
    }

    if (url.includes('campaigns/export')) {
      const exportId = url.split('/').pop();

      return `${this.getExportApiUrl(fileType)}${exportId}/${id}`;
    }

    if (url.includes('admin/user-feedback')) {
      return `${this.getAdminApiUrl(fileType)}${id}`;
    }

    return `${this.getAuthorizedApiUrl(fileType)}/${id}`;
  }

  private getAcceptHeader(fileType: TFileResourceType): string {
    if (fileType === 'image') {
      return 'image/*';
    }

    return '*/*';
  }

  private getAdminApiUrl(fileType: TFileResourceType): string {
    if (fileType === 'image') {
      return this.adminApiUrl;
    }

    return this.adminFileApiUrl;
  }

  private getAuthorizedApiUrl(fileType: TFileResourceType): string {
    if (fileType === 'image') {
      return this.authorizedApiUrl;
    }

    return this.authorizedFileApiUrl;
  }

  private getCreatorApiUrl(fileType: TFileResourceType): string {
    if (fileType === 'image') {
      return this.unauthorizedCreatorApiUrl;
    }

    return this.unauthorizedCreatorFileApiUrl;
  }

  private getExportApiUrl(fileType: TFileResourceType): string {
    if (fileType === 'image') {
      return this.unauthorizedExportApiUrl;
    }

    return this.unauthorizedExportFileApiUrl;
  }

  private static getYoutubeDimensionConstant(width?: number, height?: number): string {
    if (width === 120 || height === 90) {
      return 'default';
    }

    if (width === 320 || height === 180) {
      return 'mqdefault';
    }

    if (width === 480 || height === 360) {
      return 'hqdefault';
    }

    if (width === 640 || height === 480) {
      return 'sddefault';
    }

    if (width === 1280 || height === 720) {
      return 'maxresdefault';
    }

    return 'mqdefault';
  }
}
